/**
 * `UcpCheckoutRow` — the Endpoint page's "UCP checkout" row (ADR-0215).
 *
 * One status line (advertised / not advertised, and in which Google Pay
 * environment), a plain-English checklist of what is missing when it is
 * not, the profile URL, and — behind disclosures — shipping & tax, the
 * Google access key (ADR-0215 §11) and the two Google Pay fields a
 * merchant supplies for PRODUCTION. Owner/admin only; other roles
 * see nothing (the API refuses them).
 */

import { Check, Copy } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

import { useOptionalHttp } from '../../api/http-context.js';
import { getSiteUcp, putSiteUcp, type UcpDiscountSetting, type UcpFulfillment, type UcpReadiness, type UcpReason } from '../../api/site-ucp.js';
import { UcpDiscountCodesEditor } from './UcpDiscountCodesEditor.js';
import { UcpPlatformKeyPanel } from './UcpPlatformKeyPanel.js';
import { UcpShippingTaxEditor } from './UcpShippingTaxEditor.js';
import { useToast } from '../../app/providers.js';
import { Collapsible, Pill } from '../../components/atoms/index.js';
import { useC } from '../../lib/theme.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";

/** What a merchant does about each reason, in their words. */
export const UCP_REASON_COPY: Record<UcpReason, { label: string; fix: string }> = {
  site_not_found: { label: 'Site not found', fix: 'Reload the page.' },
  stripe_not_connected: { label: 'Stripe is not connected', fix: 'Connect Stripe under Integrations.' },
  stripe_publishable_key_missing: { label: 'Stripe needs to be reconnected once', fix: 'Google Pay tokenises on your Stripe account; reconnect Stripe under Integrations so Sill can read its public key.' },
  stripe_rail_disabled: { label: 'The Stripe rail is off for this site', fix: 'Turn it on under Integrations.' },
  settlement_rail_not_stripe: { label: 'This site settles on Shopify', fix: 'UCP checkout pays through Google Pay into Stripe. Set this site\'s settlement rail to Stripe under Integrations.' },
  policy_inactive: { label: 'No published policy', fix: 'Publish guardrails so Sill knows what to approve, escalate and refuse.' },
  google_pay_merchant_id_missing: { label: 'Google Pay merchant ID is missing', fix: 'Your Stripe account is live, so Google Pay runs in production and needs your Google Pay & Wallet Console merchant ID. Add it below.' },
};

type State =
  | { kind: 'loading' }
  | { kind: 'hidden' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; readiness: UcpReadiness };

export function UcpCheckoutRow({ site_id }: { site_id: string }): JSX.Element | null {
  const C = useC();
  const http = useOptionalHttp();
  const toast = useToast();
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [merchant_id, setMerchantId] = useState('');
  const [merchant_name, setMerchantName] = useState('');
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (http === null) return;
    let cancelled = false;
    setState({ kind: 'loading' });
    void (async () => {
      const r = await getSiteUcp(http, site_id);
      if (cancelled) return;
      if (r.kind === 'ok') {
        setState({ kind: 'ready', readiness: r.readiness });
        setMerchantId(r.readiness.google_pay.merchant_id ?? '');
        setMerchantName(r.readiness.google_pay.merchant_name ?? '');
      } else if (r.kind === 'forbidden') setState({ kind: 'hidden' });
      else setState({ kind: 'error', message: r.kind === 'not_found' ? 'Site not found.' : r.message });
    })();
    return () => { cancelled = true; };
  }, [http, site_id]);

  const save = useCallback(async () => {
    if (http === null || state.kind !== 'ready') return;
    setSaving(true);
    const r = await putSiteUcp(http, site_id, { google_pay: { merchant_id: merchant_id.trim() || null, merchant_name: merchant_name.trim() || null } });
    setSaving(false);
    if (r.kind === 'ok') {
      setState({ kind: 'ready', readiness: { ...state.readiness, checkout: r.checkout, reasons: r.reasons, capabilities: r.capabilities, google_pay: r.google_pay, fulfillment: r.fulfillment, discounts: r.discounts } });
      toast({ tone: 'lime', title: 'Google Pay details saved.', body: r.checkout ? 'Checkout is now advertised on your UCP profile.' : 'Saved. See the checklist for what is still missing.' });
    } else if (r.kind === 'bad_request') {
      toast(r.reason === 'invalid_merchant_id'
        ? { tone: 'coral', title: 'That merchant ID is not valid.', body: 'Google Pay merchant IDs are letters and digits only.' }
        : { tone: 'coral', title: 'Those details are not valid.' });
    } else {
      toast(r.kind === 'error' ? { tone: 'coral', title: 'Could not save.', body: r.message } : { tone: 'coral', title: 'Could not save.' });
    }
  }, [http, site_id, state, merchant_id, merchant_name, toast]);

  const saveFulfillment = useCallback(async (config: UcpFulfillment) => {
    if (http === null || state.kind !== 'ready') return;
    setSaving(true);
    const r = await putSiteUcp(http, site_id, { fulfillment: config });
    setSaving(false);
    if (r.kind === 'ok') {
      setState({ kind: 'ready', readiness: { ...state.readiness, checkout: r.checkout, reasons: r.reasons, capabilities: r.capabilities, google_pay: r.google_pay, fulfillment: r.fulfillment, discounts: r.discounts } });
      toast({ tone: 'lime', title: 'Shipping & tax saved.', body: r.fulfillment.shipping_options.length > 0 ? 'Platforms will now be offered these shipping options.' : 'No shipping options: checkouts collect no shipping address and add no shipping, so sell only digital items through UCP.' });
    } else {
      toast(r.kind === 'bad_request'
        ? { tone: 'coral', title: 'Those rates are not valid.', body: r.reason === 'invalid_tax_rules' ? 'Check the tax rules.' : 'Check the shipping options.' }
        : r.kind === 'error' ? { tone: 'coral', title: 'Could not save.', body: r.message } : { tone: 'coral', title: 'Could not save.' });
    }
  }, [http, site_id, state, toast]);

  const saveDiscounts = useCallback(async (setting: UcpDiscountSetting) => {
    if (http === null || state.kind !== 'ready') return;
    setSaving(true);
    const r = await putSiteUcp(http, site_id, { discounts: setting });
    setSaving(false);
    if (r.kind === 'ok') {
      setState({ kind: 'ready', readiness: { ...state.readiness, checkout: r.checkout, reasons: r.reasons, capabilities: r.capabilities, google_pay: r.google_pay, fulfillment: r.fulfillment, discounts: r.discounts } });
      toast({ tone: 'lime', title: 'Discount codes saved.', body: r.discounts.mode === 'off' ? 'Agents can\u2019t use discount codes.' : r.discounts.mode === 'all' ? 'Agents can use any active promotion code in your Stripe account.' : `Agents can use ${r.discounts.allow_list.join(', ')}.` });
    } else {
      toast(r.kind === 'bad_request'
        ? { tone: 'coral', title: 'Those codes are not valid.', body: 'Use letters, digits, - and _ only, and list at least one code.' }
        : r.kind === 'error' ? { tone: 'coral', title: 'Could not save.', body: r.message } : { tone: 'coral', title: 'Could not save.' });
    }
  }, [http, site_id, state, toast]);

  if (state.kind === 'hidden') return null;
  if (state.kind === 'loading') {
    return <div style={{ fontFamily: monoFont, fontSize: 11, color: C.muted, letterSpacing: 1 }}>Reading UCP status…</div>;
  }
  if (state.kind === 'error') {
    return <div style={{ fontFamily: sansFont, fontSize: 12, color: C.coralFg, lineHeight: 1.5 }}>Couldn&apos;t load UCP status — {state.message}</div>;
  }
  const r = state.readiness;
  const inputStyle = { width: '100%', padding: '8px 10px', background: C.bg, border: `1px solid ${C.border}`, borderRadius: 2, color: C.text, fontFamily: sansFont, fontSize: 13 } as const;

  return (
    <div className="space-y-3" data-testid="ucp-checkout-row" data-checkout={r.checkout ? 'advertised' : 'not-advertised'}>
      <div className="flex items-center gap-2 flex-wrap">
        <Pill tone={r.checkout ? 'lime' : 'amber'}>{r.checkout ? 'CHECKOUT ADVERTISED' : 'NOT ADVERTISED YET'}</Pill>
        <Pill tone="neutral">{r.environment === 'PRODUCTION' ? 'GOOGLE PAY · PRODUCTION' : 'GOOGLE PAY · TEST'}</Pill>
        <Pill tone={r.fulfillment.shipping_options.length > 0 ? 'lime' : 'neutral'}>{r.fulfillment.shipping_options.length > 0 ? `SHIPPING · ${r.fulfillment.shipping_options.length} OPTION${r.fulfillment.shipping_options.length === 1 ? '' : 'S'}` : 'DIGITAL ONLY'}</Pill>
        {r.capabilities.includes('dev.ucp.common.payment.ap2_mandate') ? <Pill tone="lime">SIGNED CHECKOUTS</Pill> : null}
        {r.discounts.mode !== 'off' ? <Pill tone="lime">{r.discounts.mode === 'all' ? 'DISCOUNT CODES · ALL' : `DISCOUNT CODES · ${r.discounts.allow_list.length}`}</Pill> : null}
      </div>
      <div style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, lineHeight: 1.5 }}>
        {r.checkout
          ? `Shopping platforms that speak UCP can read your profile, price a cart from your catalog, and pay through Google Pay into your Stripe account. Your guardrails decide; anything they escalate lands in your review queue.${r.capabilities.includes('dev.ucp.common.payment.ap2_mandate') ? ' Every cart Sill prices is signed on your behalf, and a platform that presents a shopper-approved mandate has it checked against those signed terms before anything is charged.' : ''}`
          : 'Your profile is published, but it does not offer checkout until everything below is in place. Nothing can be charged before then.'}
      </div>

      {!r.checkout ? (
        <ul className="space-y-2" data-testid="ucp-checklist" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {r.reasons.map((reason) => (
            <li key={reason} className="p-3" style={{ background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 2 }}>
              <div style={{ fontFamily: sansFont, fontSize: 13, color: C.text, fontWeight: 500 }}>{UCP_REASON_COPY[reason].label}</div>
              <div style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, marginTop: 2, lineHeight: 1.5 }}>{UCP_REASON_COPY[reason].fix}</div>
            </li>
          ))}
        </ul>
      ) : null}

      <div className="p-2.5 flex items-center gap-2" style={{ background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 2 }}>
        <span style={{ fontFamily: monoFont, fontSize: 12, color: C.text, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>{r.profile_url}</span>
        <button
          type="button"
          aria-label="Copy UCP profile URL"
          onClick={() => {
            if (typeof navigator !== 'undefined' && navigator.clipboard) void navigator.clipboard.writeText(r.profile_url);
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
          }}
          className="flex-shrink-0 p-1.5"
          style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 2 }}
        >
          {copied ? <Check size={11} color={C.limeFg} /> : <Copy size={11} color={C.muted} />}
        </button>
      </div>

      <Collapsible label="Shipping & tax" variant="inline" defaultOpen={false}>
        <UcpShippingTaxEditor key={JSON.stringify(r.fulfillment)} initial={r.fulfillment} saving={saving} onSave={(cfg) => void saveFulfillment(cfg)} />
      </Collapsible>

      <Collapsible label="Discount codes" variant="inline" defaultOpen={false}>
        <UcpDiscountCodesEditor key={JSON.stringify(r.discounts)} initial={r.discounts} saving={saving} onSave={(d) => void saveDiscounts(d)} />
      </Collapsible>

      <Collapsible label="Google access key" variant="inline" defaultOpen={false}>
        <UcpPlatformKeyPanel site_id={site_id} initial={r.platform_key} />
      </Collapsible>

      <Collapsible label="Google Pay details" variant="inline" defaultOpen={r.reasons.includes('google_pay_merchant_id_missing')}>
        <div className="space-y-3 pt-2">
          <div style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, lineHeight: 1.5 }}>
            Only needed once your Stripe account is live. Get the merchant ID from the Google Pay &amp; Wallet Console; the name is what buyers see on the Google Pay sheet.
          </div>
          <label style={{ display: 'block' }}>
            <div style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, letterSpacing: 1.5, marginBottom: 4 }}>MERCHANT ID</div>
            <input value={merchant_id} onChange={(e) => setMerchantId(e.target.value)} placeholder="e.g. BCR2DN4T…" maxLength={64} style={inputStyle} data-testid="ucp-merchant-id" />
          </label>
          <label style={{ display: 'block' }}>
            <div style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, letterSpacing: 1.5, marginBottom: 4 }}>MERCHANT NAME</div>
            <input value={merchant_name} onChange={(e) => setMerchantName(e.target.value)} placeholder="Your store name" maxLength={128} style={inputStyle} data-testid="ucp-merchant-name" />
          </label>
          <button
            type="button"
            disabled={saving}
            onClick={() => void save()}
            className="px-3 py-2"
            style={{ background: C.lime, color: '#0B0B0B', border: 'none', borderRadius: 2, fontFamily: sansFont, fontSize: 12, fontWeight: 500, opacity: saving ? 0.6 : 1 }}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </Collapsible>
    </div>
  );
}
